import { useState, useEffect } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import {
  usePersonalLoans,
  useLoanPayments,
  type PersonalLoan,
  type LoanPayment,
  type PersonalLoanType,
  type InterestType,
  type CreateLoanInput,
  type UpdateLoanInput,
  type RecordPaymentInput,
} from '@/hooks';
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  CreditCard,
  GraduationCap,
  Home,
  Car,
  Banknote,
  Heart,
  HelpCircle,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  type LucideIcon,
} from 'lucide-react';

// ─── Loan Type Config ───────────────────────────────────────────────────

const LOAN_TYPE_CONFIG: Record<
  PersonalLoanType,
  { label: string; Icon: LucideIcon; color: string }
> = {
  credit_card: { label: 'Credit Card', Icon: CreditCard, color: '#8B5CF6' },
  student_loan: { label: 'Student Loan', Icon: GraduationCap, color: '#3B82F6' },
  mortgage: { label: 'Mortgage', Icon: Home, color: '#059669' },
  auto_loan: { label: 'Auto Loan', Icon: Car, color: '#D97706' },
  personal_loan: { label: 'Personal Loan', Icon: Banknote, color: '#EC4899' },
  medical_debt: { label: 'Medical Debt', Icon: Heart, color: '#EF4444' },
  other: { label: 'Other', Icon: HelpCircle, color: '#6B7280' },
};

const INTEREST_TYPE_OPTIONS: { value: InterestType; label: string }[] = [
  { value: 'none', label: 'None / Unknown' },
  { value: 'simple', label: 'Simple' },
  { value: 'compound', label: 'Compound' },
];

// ─── Helpers ────────────────────────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatCurrencyShort(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function getNextDueDate(paymentDueDay: number | null): Date | null {
  if (!paymentDueDay) return null;
  const today = new Date();
  const thisMonth = new Date(today.getFullYear(), today.getMonth(), paymentDueDay);
  if (thisMonth >= today) return thisMonth;
  // Next month
  return new Date(today.getFullYear(), today.getMonth() + 1, paymentDueDay);
}

function isDuePast(paymentDueDay: number | null): boolean {
  if (!paymentDueDay) return false;
  const today = new Date();
  const currentDay = today.getDate();
  return currentDay > paymentDueDay;
}

function isPaymentFulfilled(
  paymentDueDay: number | null,
  lastPaymentDate: string | null
): boolean {
  if (!paymentDueDay || !lastPaymentDate) return false;
  const today = new Date();
  const lastPayment = new Date(lastPaymentDate + 'T00:00:00');
  const thisMonthDue = new Date(today.getFullYear(), today.getMonth(), paymentDueDay);
  // Current billing cycle starts at the previous due date
  const cycleStart =
    today < thisMonthDue
      ? new Date(today.getFullYear(), today.getMonth() - 1, paymentDueDay)
      : thisMonthDue;
  return lastPayment >= cycleStart;
}

function formatDueDate(paymentDueDay: number | null): string {
  const next = getNextDueDate(paymentDueDay);
  if (!next) return 'No due date';
  return next.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Types ──────────────────────────────────────────────────────────────

type DialogMode = 'create' | 'edit' | 'payment' | 'editPayment' | null;

interface LoanFormState {
  name: string;
  lender: string;
  loan_type: PersonalLoanType;
  original_amount: string;
  current_balance: string;
  interest_rate: string;
  interest_type: InterestType;
  minimum_payment: string;
  payment_due_day: string;
  notes: string;
}

const EMPTY_FORM: LoanFormState = {
  name: '',
  lender: '',
  loan_type: 'credit_card',
  original_amount: '',
  current_balance: '',
  interest_rate: '',
  interest_type: 'none',
  minimum_payment: '',
  payment_due_day: '',
  notes: '',
};

interface PaymentFormState {
  payment_date: string;
  amount_paid: string;
  new_balance: string;
  notes: string;
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

const EMPTY_PAYMENT: PaymentFormState = {
  payment_date: todayISO(),
  amount_paid: '',
  new_balance: '',
  notes: '',
};

// ─── PaymentHistory ─────────────────────────────────────────────────────

function PaymentHistory({
  loanId,
  onEdit,
  onDelete,
}: {
  loanId: string;
  onEdit: (payment: LoanPayment) => void;
  onDelete: (payment: LoanPayment) => void;
}) {
  const { payments, isLoading } = useLoanPayments(loanId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (payments.length === 0) {
    return (
      <p className="py-3 text-center text-sm text-muted-foreground">
        No payments recorded yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {payments.map((p) => (
        <div
          key={p.id}
          className="flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2 text-sm"
        >
          <div>
            <span className="font-medium">
              {new Date(p.payment_date + 'T00:00:00').toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
            {p.notes && (
              <span className="ml-2 text-muted-foreground">— {p.notes}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <span className="font-medium text-sapling-primary-100">
                -{formatCurrency(parseFloat(p.amount_paid))}
              </span>
              <span className="ml-2 text-muted-foreground">
                bal: {formatCurrency(parseFloat(p.new_balance))}
              </span>
            </div>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(p)}>
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onDelete(p)}>
              <Trash2 className="h-3.5 w-3.5 text-destructive" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── LoanCard ───────────────────────────────────────────────────────────

function LoanCard({
  loan,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onRecordPayment,
  onEditPayment,
  onDeletePayment,
}: {
  loan: PersonalLoan;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRecordPayment: () => void;
  onEditPayment: (payment: LoanPayment) => void;
  onDeletePayment: (payment: LoanPayment) => void;
}) {
  const config = LOAN_TYPE_CONFIG[loan.loan_type];
  const TypeIcon = config.Icon;
  const original = parseFloat(loan.original_amount);
  const balance = parseFloat(loan.current_balance);
  const paidOff = original - balance;
  const percentage = original > 0 ? Math.min((paidOff / original) * 100, 100) : 0;
  const fulfilled = isPaymentFulfilled(loan.payment_due_day, loan.last_payment_date);
  const pastDue = loan.status === 'active' && isDuePast(loan.payment_due_day) && !fulfilled;

  return (
    <div className="border-b border-border py-5 last:border-b-0">
      {/* Header row */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-lg"
            style={{ backgroundColor: `${config.color}20` }}
          >
            <TypeIcon className="h-5 w-5" style={{ color: config.color }} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-foreground">{loan.name}</h3>
              <span
                className="rounded-full px-2 py-0.5 text-xs font-medium"
                style={{ backgroundColor: `${config.color}15`, color: config.color }}
              >
                {config.label}
              </span>
              {loan.status === 'paid_off' && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600">
                  <CheckCircle2 className="h-3 w-3" /> Paid Off
                </span>
              )}
              {pastDue && (
                <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">
                  <AlertTriangle className="h-3 w-3" /> Past Due
                </span>
              )}
              {loan.status === 'active' && fulfilled && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-500/10 px-2 py-0.5 text-xs font-medium text-green-600">
                  <CheckCircle2 className="h-3 w-3" /> Paid
                </span>
              )}
            </div>
            {loan.lender && (
              <p className="text-sm text-muted-foreground">{loan.lender}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {/* Balance + progress */}
      <div className="mb-2 flex items-baseline justify-between text-sm">
        <span className="text-muted-foreground">
          {formatCurrency(balance)} remaining of {formatCurrencyShort(original)}
        </span>
        <span className="font-medium text-foreground">{Math.round(percentage)}% paid</span>
      </div>

      <div className="mb-3 h-2.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${percentage}%`,
            backgroundColor: config.color,
          }}
        />
      </div>

      {/* Details row */}
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
        {loan.minimum_payment && (
          <span>Payment: {formatCurrency(parseFloat(loan.minimum_payment))}/mo</span>
        )}
        {loan.payment_due_day && (
          <span className={pastDue ? 'font-medium text-destructive' : ''}>
            Due: {formatDueDate(loan.payment_due_day)}
          </span>
        )}
        {loan.interest_rate && (
          <span>
            Rate: {(parseFloat(loan.interest_rate) * 100).toFixed(2)}%
            {loan.interest_type !== 'none' && ` (${loan.interest_type})`}
          </span>
        )}
      </div>

      {loan.notes && (
        <p className="mb-3 text-sm italic text-muted-foreground">{loan.notes}</p>
      )}

      {/* Actions row */}
      <div className="flex items-center gap-2">
        {loan.status === 'active' && (
          <Button variant="outline" size="sm" onClick={onRecordPayment}>
            <Plus className="mr-1 h-4 w-4" />
            Record Payment
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onToggle}>
          {isExpanded ? (
            <>
              <ChevronUp className="mr-1 h-4 w-4" />
              Hide History
            </>
          ) : (
            <>
              <ChevronDown className="mr-1 h-4 w-4" />
              Payment History
            </>
          )}
        </Button>
      </div>

      {/* Expanded payment history */}
      {isExpanded && (
        <div className="mt-4 rounded-lg border bg-background p-4">
          <h4 className="mb-3 text-sm font-semibold text-foreground">Payment History</h4>
          <PaymentHistory
            loanId={loan.id}
            onEdit={onEditPayment}
            onDelete={onDeletePayment}
          />
        </div>
      )}
    </div>
  );
}

// ─── PersonalLoansPage ──────────────────────────────────────────────────

export function PersonalLoansPage() {
  const {
    loans, isLoading, createLoan, updateLoan, deleteLoan,
    recordPayment, updatePayment, deletePayment,
  } = usePersonalLoans({ includeInactive: true });

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [editingLoan, setEditingLoan] = useState<PersonalLoan | null>(null);
  const [paymentLoanId, setPaymentLoanId] = useState<string | null>(null);
  const [editingPayment, setEditingPayment] = useState<LoanPayment | null>(null);
  const [form, setForm] = useState<LoanFormState>(EMPTY_FORM);
  const [paymentForm, setPaymentForm] = useState<PaymentFormState>(EMPTY_PAYMENT);
  const [expandedLoanId, setExpandedLoanId] = useState<string | null>(null);

  const isSaving =
    createLoan.isPending || updateLoan.isPending || recordPayment.isPending ||
    updatePayment.isPending || deletePayment.isPending;

  // Auto-open create dialog if ?action=add is in URL
  const searchParams = useSearch({ strict: false }) as Record<string, string>;
  useEffect(() => {
    if (searchParams.action === 'add') {
      setForm(EMPTY_FORM);
      setEditingLoan(null);
      setDialogMode('create');
    }
  }, []);

  // ── Active vs inactive loans
  const activeLoans = loans.filter((l) => l.status === 'active');
  const inactiveLoans = loans.filter((l) => l.status !== 'active');

  // ── Dialog handlers
  function openCreate() {
    setForm(EMPTY_FORM);
    setEditingLoan(null);
    setDialogMode('create');
  }

  function openEdit(loan: PersonalLoan) {
    setForm({
      name: loan.name,
      lender: loan.lender || '',
      loan_type: loan.loan_type,
      original_amount: parseFloat(loan.original_amount).toString(),
      current_balance: parseFloat(loan.current_balance).toString(),
      interest_rate: loan.interest_rate
        ? (parseFloat(loan.interest_rate) * 100).toString()
        : '',
      interest_type: loan.interest_type,
      minimum_payment: loan.minimum_payment
        ? parseFloat(loan.minimum_payment).toString()
        : '',
      payment_due_day: loan.payment_due_day?.toString() || '',
      notes: loan.notes || '',
    });
    setEditingLoan(loan);
    setDialogMode('edit');
  }

  function openPayment(loanId: string) {
    const loan = loans.find((l) => l.id === loanId);
    setPaymentLoanId(loanId);
    setPaymentForm({
      ...EMPTY_PAYMENT,
      payment_date: todayISO(),
      new_balance: loan ? parseFloat(loan.current_balance).toString() : '',
    });
    setDialogMode('payment');
  }

  function closeDialog() {
    setDialogMode(null);
    setEditingLoan(null);
    setPaymentLoanId(null);
    setEditingPayment(null);
  }

  function openEditPayment(loanId: string, payment: LoanPayment) {
    setPaymentLoanId(loanId);
    setEditingPayment(payment);
    setPaymentForm({
      payment_date: payment.payment_date,
      amount_paid: parseFloat(payment.amount_paid).toString(),
      new_balance: parseFloat(payment.new_balance).toString(),
      notes: payment.notes || '',
    });
    setDialogMode('editPayment');
  }

  async function handleUpdatePayment() {
    if (!paymentLoanId || !editingPayment) return;
    const amountPaid = parseFloat(paymentForm.amount_paid);
    const newBalance = parseFloat(paymentForm.new_balance);

    if (!paymentForm.payment_date) {
      toast({ variant: 'destructive', title: 'Payment date is required' });
      return;
    }
    if (isNaN(amountPaid) || amountPaid <= 0) {
      toast({ variant: 'destructive', title: 'Amount paid must be a positive number' });
      return;
    }
    if (isNaN(newBalance) || newBalance < 0) {
      toast({ variant: 'destructive', title: 'New balance must be zero or positive' });
      return;
    }

    try {
      await updatePayment.mutateAsync({
        loanId: paymentLoanId,
        paymentId: editingPayment.id,
        payment_date: paymentForm.payment_date,
        amount_paid: amountPaid,
        new_balance: newBalance,
        notes: paymentForm.notes.trim() || null,
      });
      toast({ title: 'Payment updated' });
      closeDialog();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  async function handleDeletePayment(loanId: string, payment: LoanPayment) {
    if (!confirm(`Delete payment of ${formatCurrency(parseFloat(payment.amount_paid))} from ${new Date(payment.payment_date + 'T00:00:00').toLocaleDateString()}? The loan balance will be recalculated.`))
      return;
    try {
      await deletePayment.mutateAsync({ loanId, paymentId: payment.id });
      toast({ title: 'Payment deleted' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  async function handleSaveLoan() {
    if (!form.name.trim()) {
      toast({ variant: 'destructive', title: 'Name is required' });
      return;
    }
    const originalAmount = parseFloat(form.original_amount);
    const currentBalance = parseFloat(form.current_balance);
    if (isNaN(originalAmount) || originalAmount <= 0) {
      toast({ variant: 'destructive', title: 'Original amount must be a positive number' });
      return;
    }
    if (isNaN(currentBalance) || currentBalance < 0) {
      toast({ variant: 'destructive', title: 'Current balance must be zero or positive' });
      return;
    }

    const interestRate = form.interest_rate
      ? parseFloat(form.interest_rate) / 100
      : undefined;
    const minimumPayment = form.minimum_payment
      ? parseFloat(form.minimum_payment)
      : undefined;
    const paymentDueDay = form.payment_due_day
      ? parseInt(form.payment_due_day, 10)
      : undefined;

    if (interestRate !== undefined && (isNaN(interestRate) || interestRate < 0)) {
      toast({ variant: 'destructive', title: 'Interest rate must be a positive number' });
      return;
    }
    if (paymentDueDay !== undefined && (paymentDueDay < 1 || paymentDueDay > 31)) {
      toast({ variant: 'destructive', title: 'Payment due day must be between 1 and 31' });
      return;
    }

    try {
      if (dialogMode === 'create') {
        const input: CreateLoanInput = {
          name: form.name.trim(),
          lender: form.lender.trim() || undefined,
          loan_type: form.loan_type,
          original_amount: originalAmount,
          current_balance: currentBalance,
          interest_rate: interestRate,
          interest_type: form.interest_type,
          minimum_payment: minimumPayment,
          payment_due_day: paymentDueDay,
          notes: form.notes.trim() || undefined,
        };
        await createLoan.mutateAsync(input);
        toast({ title: 'Loan added' });
      } else if (dialogMode === 'edit' && editingLoan) {
        const input: UpdateLoanInput & { id: string } = {
          id: editingLoan.id,
          name: form.name.trim(),
          lender: form.lender.trim() || null,
          loan_type: form.loan_type,
          original_amount: originalAmount,
          current_balance: currentBalance,
          interest_rate: interestRate ?? null,
          interest_type: form.interest_type,
          minimum_payment: minimumPayment ?? null,
          payment_due_day: paymentDueDay ?? null,
          notes: form.notes.trim() || null,
        };
        await updateLoan.mutateAsync(input);
        toast({ title: 'Loan updated' });
      }
      closeDialog();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  async function handleRecordPayment() {
    if (!paymentLoanId) return;
    const amountPaid = parseFloat(paymentForm.amount_paid);
    const newBalance = parseFloat(paymentForm.new_balance);

    if (!paymentForm.payment_date) {
      toast({ variant: 'destructive', title: 'Payment date is required' });
      return;
    }
    if (isNaN(amountPaid) || amountPaid <= 0) {
      toast({ variant: 'destructive', title: 'Amount paid must be a positive number' });
      return;
    }
    if (isNaN(newBalance) || newBalance < 0) {
      toast({ variant: 'destructive', title: 'New balance must be zero or positive' });
      return;
    }

    try {
      const input: RecordPaymentInput & { loanId: string } = {
        loanId: paymentLoanId,
        payment_date: paymentForm.payment_date,
        amount_paid: amountPaid,
        new_balance: newBalance,
        notes: paymentForm.notes.trim() || undefined,
      };
      await recordPayment.mutateAsync(input);
      toast({
        title: newBalance === 0 ? 'Loan paid off!' : 'Payment recorded',
        description:
          newBalance === 0
            ? 'Congratulations! This loan has been marked as paid off.'
            : `Recorded ${formatCurrency(amountPaid)} payment.`,
      });
      closeDialog();
      // Expand history for this loan
      setExpandedLoanId(paymentLoanId);
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  async function handleDelete(loan: PersonalLoan) {
    if (!confirm(`Delete "${loan.name}"? This will also delete all payment history and cannot be undone.`))
      return;
    try {
      await deleteLoan.mutateAsync(loan.id);
      toast({ title: 'Loan deleted' });
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Something went wrong',
      });
    }
  }

  // ── Totals
  const totalDebt = activeLoans.reduce(
    (sum, l) => sum + parseFloat(l.current_balance),
    0
  );
  const totalMonthly = activeLoans.reduce(
    (sum, l) => sum + (l.minimum_payment ? parseFloat(l.minimum_payment) : 0),
    0
  );

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 py-8">
        <div className="container max-w-3xl">
          {/* Back link */}
          <Link
            to="/app/personal/dashboard"
            className="mb-6 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Back to Personal Dashboard
          </Link>

          {/* Page header */}
          <div className="mb-8 flex items-center justify-between">
            <h1 className="text-2xl font-bold text-foreground">Loans & Debts</h1>
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Loan
            </Button>
          </div>

          {/* Loading */}
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty state */}
          {!isLoading && loans.length === 0 && (
            <div className="mt-12 rounded-card border border-dashed bg-muted/50 p-8 text-center">
              <Banknote className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="mb-1 font-medium text-foreground">No loans tracked yet</p>
              <p className="mb-4 text-sm text-muted-foreground">
                Add your loans and debts to track balances and payment schedules.
              </p>
              <Button onClick={openCreate} variant="outline">
                <Plus className="mr-2 h-4 w-4" />
                Add Loan
              </Button>
            </div>
          )}

          {/* Summary row */}
          {!isLoading && activeLoans.length > 0 && (
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div className="rounded-card border bg-card p-4">
                <p className="text-sm text-muted-foreground">Total Outstanding</p>
                <p className="text-xl font-bold text-foreground">
                  {formatCurrency(totalDebt)}
                </p>
              </div>
              <div className="rounded-card border bg-card p-4">
                <p className="text-sm text-muted-foreground">Monthly Payments</p>
                <p className="text-xl font-bold text-foreground">
                  {formatCurrency(totalMonthly)}
                </p>
              </div>
            </div>
          )}

          {/* Active loans */}
          {!isLoading && activeLoans.length > 0 && (
            <div className="rounded-card border bg-card p-6">
              {activeLoans.map((loan) => (
                <LoanCard
                  key={loan.id}
                  loan={loan}
                  isExpanded={expandedLoanId === loan.id}
                  onToggle={() =>
                    setExpandedLoanId(expandedLoanId === loan.id ? null : loan.id)
                  }
                  onEdit={() => openEdit(loan)}
                  onDelete={() => handleDelete(loan)}
                  onRecordPayment={() => openPayment(loan.id)}
                  onEditPayment={(p) => openEditPayment(loan.id, p)}
                  onDeletePayment={(p) => handleDeletePayment(loan.id, p)}
                />
              ))}
            </div>
          )}

          {/* Paid off / cancelled loans */}
          {!isLoading && inactiveLoans.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-4 text-lg font-semibold text-muted-foreground">
                Paid Off & Cancelled
              </h2>
              <div className="rounded-card border bg-card p-6 opacity-75">
                {inactiveLoans.map((loan) => (
                  <LoanCard
                    key={loan.id}
                    loan={loan}
                    isExpanded={expandedLoanId === loan.id}
                    onToggle={() =>
                      setExpandedLoanId(expandedLoanId === loan.id ? null : loan.id)
                    }
                    onEdit={() => openEdit(loan)}
                    onDelete={() => handleDelete(loan)}
                    onRecordPayment={() => {}}
                    onEditPayment={(p) => openEditPayment(loan.id, p)}
                    onDeletePayment={(p) => handleDeletePayment(loan.id, p)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ── Create / Edit Loan Dialog ──────────────────────────────────── */}
      <Dialog
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' ? 'Add Loan' : 'Edit Loan'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Name */}
            <div>
              <Label htmlFor="loan-name">Name</Label>
              <Input
                id="loan-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Chase Sapphire, Student Loan #1, etc."
              />
            </div>

            {/* Lender */}
            <div>
              <Label htmlFor="loan-lender">Lender (optional)</Label>
              <Input
                id="loan-lender"
                value={form.lender}
                onChange={(e) => setForm({ ...form, lender: e.target.value })}
                placeholder="Chase, Navient, Wells Fargo, etc."
              />
            </div>

            {/* Loan Type */}
            <div>
              <Label htmlFor="loan-type">Loan Type</Label>
              <select
                id="loan-type"
                value={form.loan_type}
                onChange={(e) =>
                  setForm({ ...form, loan_type: e.target.value as PersonalLoanType })
                }
                className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {Object.entries(LOAN_TYPE_CONFIG).map(([value, { label }]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Original Amount + Current Balance */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="loan-original">Original Amount ($)</Label>
                <Input
                  id="loan-original"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.original_amount}
                  onChange={(e) =>
                    setForm({ ...form, original_amount: e.target.value })
                  }
                  placeholder="25000"
                />
              </div>
              <div>
                <Label htmlFor="loan-balance">Current Balance ($)</Label>
                <Input
                  id="loan-balance"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.current_balance}
                  onChange={(e) =>
                    setForm({ ...form, current_balance: e.target.value })
                  }
                  placeholder="18500"
                />
              </div>
            </div>

            {/* Interest Rate + Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="loan-rate">Interest Rate % (optional)</Label>
                <Input
                  id="loan-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.interest_rate}
                  onChange={(e) =>
                    setForm({ ...form, interest_rate: e.target.value })
                  }
                  placeholder="5.25"
                />
              </div>
              <div>
                <Label htmlFor="loan-interest-type">Interest Type</Label>
                <select
                  id="loan-interest-type"
                  value={form.interest_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      interest_type: e.target.value as InterestType,
                    })
                  }
                  className="flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {INTEREST_TYPE_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Min Payment + Due Day */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="loan-payment">Min. Monthly Payment ($)</Label>
                <Input
                  id="loan-payment"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.minimum_payment}
                  onChange={(e) =>
                    setForm({ ...form, minimum_payment: e.target.value })
                  }
                  placeholder="350"
                />
              </div>
              <div>
                <Label htmlFor="loan-due-day">Payment Due Day (1-31)</Label>
                <Input
                  id="loan-due-day"
                  type="number"
                  min="1"
                  max="31"
                  value={form.payment_due_day}
                  onChange={(e) =>
                    setForm({ ...form, payment_due_day: e.target.value })
                  }
                  placeholder="15"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="loan-notes">Notes (optional)</Label>
              <Input
                id="loan-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="0% APR until June 2027, etc."
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleSaveLoan} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {dialogMode === 'create' ? 'Add' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Record Payment Dialog ──────────────────────────────────────── */}
      <Dialog
        open={dialogMode === 'payment'}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Payment Date */}
            <div>
              <Label htmlFor="pay-date">Payment Date</Label>
              <Input
                id="pay-date"
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, payment_date: e.target.value })
                }
              />
            </div>

            {/* Amount Paid */}
            <div>
              <Label htmlFor="pay-amount">Amount Paid ($)</Label>
              <Input
                id="pay-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={paymentForm.amount_paid}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, amount_paid: e.target.value })
                }
                placeholder="350"
                autoFocus
              />
            </div>

            {/* New Balance */}
            <div>
              <Label htmlFor="pay-balance">New Remaining Balance ($)</Label>
              <Input
                id="pay-balance"
                type="number"
                min="0"
                step="0.01"
                value={paymentForm.new_balance}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, new_balance: e.target.value })
                }
                placeholder="18150"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Enter the balance remaining after this payment.
                Set to 0 to mark the loan as paid off.
              </p>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="pay-notes">Notes (optional)</Label>
              <Input
                id="pay-notes"
                value={paymentForm.notes}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, notes: e.target.value })
                }
                placeholder="Extra payment toward principal"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleRecordPayment} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Record
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Payment Dialog ────────────────────────────────────────── */}
      <Dialog
        open={dialogMode === 'editPayment'}
        onOpenChange={(open) => {
          if (!open) closeDialog();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Payment</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="edit-pay-date">Payment Date</Label>
              <Input
                id="edit-pay-date"
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, payment_date: e.target.value })
                }
              />
            </div>

            <div>
              <Label htmlFor="edit-pay-amount">Amount Paid ($)</Label>
              <Input
                id="edit-pay-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={paymentForm.amount_paid}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, amount_paid: e.target.value })
                }
                autoFocus
              />
            </div>

            <div>
              <Label htmlFor="edit-pay-balance">New Remaining Balance ($)</Label>
              <Input
                id="edit-pay-balance"
                type="number"
                min="0"
                step="0.01"
                value={paymentForm.new_balance}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, new_balance: e.target.value })
                }
              />
              <p className="mt-1 text-xs text-muted-foreground">
                The loan balance will be recalculated based on the most recent payment.
              </p>
            </div>

            <div>
              <Label htmlFor="edit-pay-notes">Notes (optional)</Label>
              <Input
                id="edit-pay-notes"
                value={paymentForm.notes}
                onChange={(e) =>
                  setPaymentForm({ ...paymentForm, notes: e.target.value })
                }
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button onClick={handleUpdatePayment} disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}
